import type { Auth0ContextInterface } from '@auth0/auth0-react';
import { EVENTS, track } from '@shared/helpers/analytics';
import { captureUtm, utmProps } from '@shared/helpers/utm';
import type { QueryClient } from '@tanstack/react-query';
import {
  createRootRouteWithContext,
  createRoute,
  notFound,
  redirect,
} from '@tanstack/react-router';
import { isDemoMode } from '../auth/demo-auth-provider';
import { getTrialSession } from '../auth/trial-session';
import { FourOhFour } from '../components/404';
import { RootLayout } from '../components/layouts/root-layout';
import { FullPageError } from '../components/route-errors';
import { PreviewComponents } from '../pages/preview/components';
import { PreviewIndex } from '../pages/preview/index';
import { PreviewTokens } from '../pages/preview/tokens';
import { Signup } from '../pages/signup';

// ---------------------------------------------------------------------------
// Router context
// ---------------------------------------------------------------------------

export interface RouterContext {
  auth: Auth0ContextInterface;
  queryClient: QueryClient;
}

// ---------------------------------------------------------------------------
// Root route
// ---------------------------------------------------------------------------

export const rootRoute = createRootRouteWithContext<RouterContext>()({
  component: RootLayout,
  errorComponent: FullPageError,
  notFoundComponent: FourOhFour,
});

// ---------------------------------------------------------------------------
// Public routes (no auth required)
// ---------------------------------------------------------------------------

// Dev-only design-system preview surfaces. Three pages: an index (narrative,
// Named Rules, Do's/Don'ts), a foundations page (live tokens read from CSS
// custom properties, with a demo-theme toggle), and a components catalog
// (every primitive in its real chrome). `import.meta.env.DEV` is replaced by
// Vite at build time so the route entries (and any reachable side of the
// ternary) are dead-code-eliminated from prod. The preview modules themselves
// still get bundled via the static imports above; promote to dynamic imports
// if a bundle audit ever flags them.
export const previewRoutes = import.meta.env.DEV
  ? [
      createRoute({
        getParentRoute: () => rootRoute,
        path: '/preview',
        component: PreviewIndex,
      }),
      createRoute({
        getParentRoute: () => rootRoute,
        path: '/preview/tokens',
        component: PreviewTokens,
      }),
      createRoute({
        getParentRoute: () => rootRoute,
        path: '/preview/components',
        component: PreviewComponents,
      }),
    ]
  : [];

// A trial store's link (ORD-514). Needs no Auth0: initTrialSession has already
// saved the session from this URL at boot, and the trial auth provider is
// running the app. All that's left is to drop the token from the address bar
// and the history, and open the store.
export const trialLinkRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/t/$storeId/$token',
  beforeLoad: ({ params }) => {
    // The demo site never starts a trial session (index.tsx).
    if (isDemoMode()) {
      throw notFound();
    }

    // Reached by navigating inside an app that booted without this session
    // (an Auth0 one, or another trial's): boot again so it starts.
    if (getTrialSession()?.storeId !== params.storeId) {
      window.location.reload();

      return;
    }

    throw redirect({ to: '/stores/$storeId', params: { storeId: params.storeId }, replace: true });
  },
});

export const signupRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/signup',
  beforeLoad: async ({ context }) => {
    if (context.auth.isAuthenticated) {
      throw redirect({ to: '/onboard', search: { checkout: undefined } });
    }

    // Stash attribution before we navigate away to Auth0, and mark signup start.
    const utm = captureUtm('local');
    track(EVENTS.signupStarted, utmProps(utm));

    // Fire-and-forget: the browser will navigate to Auth0 shortly.
    // Don't block — let the Signup component render "Redirecting..." message.
    context.auth.loginWithRedirect({
      appState: { returnTo: '/onboard' },
    });
  },
  component: Signup,
});
